import type { LinkableDlnItemsLookupParams } from './dlnItemLinks/types';
import type { InvoiceEmailSuggestionKind } from './useQueryInvoiceEmailSuggestion/types';
import type { InvoiceFileType } from './useQueryInvoiceFile/types';
import type { InvoiceFileRole } from './useQueryInvoiceFileByRole/types';
import type { InvoiceOwnerCompanySuggestionsQueryParams } from './useQueryInvoiceOwnerCompanySuggestions/types';
import type {
  InvoicePartiesQueryParams,
  InvoicePartyLookupParams,
} from './useQueryInvoiceParties';
import type { InvoicePossibleAssigneesPagedParams } from './useQueryInvoicePossibleAssignees/types';
import type { InvoicesQueryParams } from './useQueryInvoices';
import type { InvoicesV2QueryParams } from './useQueryInvoicesV2/types';
import type { InvoiceV2AssigneeSuggestionsQueryParams } from './useQueryInvoiceV2AssigneeSuggestions/types';
import type { InvoiceV2FilterOptionSuggestionsLookupParams } from './useQueryInvoiceV2FilterOptionSuggestions/types';
import type {
  InvoiceV2PartyCompanyLookupParams,
  InvoiceV2PartySuggestionsLookupParams,
} from './useQueryInvoiceV2PartySuggestions/types';
import type { LinkableDeliveryNotesQueryParams } from './useQueryLinkableDeliveryNotes/types';

import type { UUID } from '~/types/common';

/**
 * Query key factory for invoice-related queries.
 */
export const queryKeysInvoice = {
  base: () => 'invoices' as const,
  get: (invoiceId: UUID) =>
    [queryKeysInvoice.base(), 'detail', { invoiceId }] as const,
  getAll: (queryParams: Partial<InvoicesQueryParams>) =>
    [queryKeysInvoice.base(), 'list', queryParams] as const,
  getAllV2: (queryParams: Partial<InvoicesV2QueryParams>) =>
    [queryKeysInvoice.base(), 'list-v2', queryParams] as const,
  getAssigneeFilterOption: (option: { id: string; label: string }) =>
    [queryKeysInvoice.base(), 'assignee-filter', 'detail', option] as const,
  getAssigneeFilterOptions: (queryParams: {
    offset: number;
    searchString?: string;
    staticOptions: readonly { id: string; label: string }[];
  }) =>
    [queryKeysInvoice.base(), 'assignee-filter', 'list', queryParams] as const,
  getAssigneeFilterUser: (queryParams: { userId: UUID }) =>
    [queryKeysInvoice.base(), 'assignee-filter', 'user', queryParams] as const,
  getAssigneeSuggestions: (
    queryParams: Partial<InvoiceV2AssigneeSuggestionsQueryParams>,
  ) =>
    [
      queryKeysInvoice.base(),
      'assignee-suggestions',
      'list',
      queryParams,
    ] as const,
  getDetail: (invoiceId: UUID) =>
    [queryKeysInvoice.base(), 'new-detail', { invoiceId }] as const,
  getEmail: (invoiceId: UUID, fileId: UUID) =>
    [queryKeysInvoice.base(), 'email', { invoiceId, fileId }] as const,
  getEmailSuggestion: (invoiceId: UUID, kind: InvoiceEmailSuggestionKind) =>
    [queryKeysInvoice.base(), 'email-suggestion', { invoiceId, kind }] as const,
  getExportAttempt: (invoiceId: UUID, attemptId: UUID) =>
    [
      queryKeysInvoice.base(),
      'export-attempt',
      'detail',
      { attemptId, invoiceId },
    ] as const,
  getFileById: (invoiceId: UUID, fileId: UUID) =>
    [queryKeysInvoice.base(), 'file-by-id', { invoiceId, fileId }] as const,
  getFileByRole: (invoiceId: UUID, role: InvoiceFileRole) =>
    [queryKeysInvoice.base(), 'file-by-role', { invoiceId, role }] as const,
  getFileByType: (invoiceId: UUID, fileType: InvoiceFileType) =>
    [queryKeysInvoice.base(), 'file', { invoiceId, fileType }] as const,
  getFilterOptionSuggestions: (
    queryParams: InvoiceV2FilterOptionSuggestionsLookupParams,
  ) =>
    [
      queryKeysInvoice.base(),
      'filter-option-suggestions',
      'list',
      queryParams,
    ] as const,
  getFilterParties: (queryParams: Partial<InvoicePartiesQueryParams>) =>
    [queryKeysInvoice.base(), 'filter-parties', 'list', queryParams] as const,
  getFilterParty: (queryParams: InvoicePartyLookupParams) =>
    [queryKeysInvoice.base(), 'filter-parties', 'detail', queryParams] as const,
  getLinkableDlnItems: (queryParams: LinkableDlnItemsLookupParams) =>
    [
      ...queryKeysInvoice.getLinkableDlnItemsAll(),
      'list',
      queryParams,
    ] as const,
  getLinkableDlnItemsAll: () =>
    [queryKeysInvoice.base(), 'linkable-dln-items'] as const,
  getLinkableDlnItemsSelection: (queryParams: LinkableDlnItemsLookupParams) =>
    [
      ...queryKeysInvoice.getLinkableDlnItemsAll(),
      'selection',
      queryParams,
    ] as const,
  getOwnerCompanySuggestion: (companyId: UUID) =>
    [
      queryKeysInvoice.base(),
      'owner-company-suggestions',
      'detail',
      { companyId },
    ] as const,
  getOwnerCompanySuggestions: (
    queryParams: Partial<InvoiceOwnerCompanySuggestionsQueryParams>,
  ) =>
    [
      queryKeysInvoice.base(),
      'owner-company-suggestions',
      queryParams,
    ] as const,
  getPartyCompany: (queryParams: InvoiceV2PartyCompanyLookupParams) =>
    [
      queryKeysInvoice.base(),
      'party-companies',
      'detail',
      queryParams,
    ] as const,
  getPartySuggestions: (queryParams: InvoiceV2PartySuggestionsLookupParams) =>
    [
      queryKeysInvoice.base(),
      'party-suggestions',
      'list',
      queryParams,
    ] as const,
  getLinkableDeliveryNotes: (queryParams: LinkableDeliveryNotesQueryParams) =>
    [queryKeysInvoice.base(), 'linkable-delivery-notes', queryParams] as const,
  getPdf: (invoiceId: UUID) =>
    [queryKeysInvoice.base(), 'pdf', { invoiceId }] as const,
  getPossibleAssignees: (invoiceId: UUID) =>
    [queryKeysInvoice.base(), 'possible-assignees', { invoiceId }] as const,
  getPossibleAssigneesPaged: (
    queryParams: InvoicePossibleAssigneesPagedParams,
  ) =>
    [
      queryKeysInvoice.base(),
      'possible-assignees',
      'paged',
      queryParams,
    ] as const,
  getTimeline: (invoiceId: UUID) =>
    [queryKeysInvoice.base(), 'timeline', { invoiceId }] as const,
} as const;
